import { Dispatch } from 'react'
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useInfiniteQuery,
  useQueryClient,
} from 'react-query'
import {
  BatchConversationOperation,
  Conversation,
  ConversationContext,
  ErrorResponse,
  ConversationList,
  MessagesApi,
} from '@citruscamps/citrus-client'
import {
  DefaultDetailsPagination,
  DefaultMainPagination,
  UnlimitedPagination,
} from '../../../../../constants/pagination'
import { useLocalStorage } from '../../../../../hooks/useLocalStorage'
import { useRouter } from '../../../../../hooks/useRouter'
import { useSearchValue } from '../../../../../hooks/useSearchValue'
import { useToast } from '../../../../../hooks/useToast'
import { Pagination } from '../../../../../interfaces/pagination'
import translations from '../../../../../translations/en.json'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey, generateListKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'

const DefaultFetchProps: ConversationFetchProps = {
  sort: 'updated',
  order: 'DESC',
  filter: {
    conversation_id: '',
    has_recieved: true,
    is_archived: false,
  },
}

export interface ConversationFilterProps {
  conversation_id?: string
  has_recieved?: boolean
  has_sent?: boolean
  is_open?: boolean
  is_archived?: boolean
  context?: ConversationContext[]
}

export interface ConversationFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  filter: ConversationFilterProps
}

interface IProps {
  programId?: string
  pagination?: Pagination
  fetchProps?: ConversationFetchProps
  overwriteFilters?: boolean
  enabled?: boolean
}

interface FetchConversations {
  data: Conversation[]
  total: any
  error: ErrorResponse | null
  fetchProps: ConversationFetchProps
  isError: boolean
  isFetching: boolean
  isSearching: boolean
  hasNextPage: boolean
  search: string
  pagination: Pagination
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<ConversationList, ErrorResponse>>
  handleArchive: (conversationId: string) => Promise<void> | void
  handleBulkArchive: (conversations: Conversation[], update_all?: boolean) => Promise<void> | void
  handleBulkMarkAsClosed: (
    conversations: Conversation[],
    update_all?: boolean,
  ) => Promise<void> | void
  handleBulkMarkAsOpen: (
    conversations: Conversation[],
    update_all?: boolean,
  ) => Promise<void> | void
  handleBulkUnArchive: (conversations: Conversation[], update_all?: boolean) => Promise<void> | void
  handleMarkAsClosed: (conversationId: string) => Promise<void> | void
  handleMarkAsOpen: (conversationId: string) => Promise<void> | void
  handleUnArchive: (conversationId: string) => Promise<void> | void
  setFetchProps: (value?: ConversationFetchProps) => void
  setSearch: Dispatch<string>
}

export const useFetchConversations = ({
  programId = '',
  pagination = DefaultDetailsPagination,
  overwriteFilters,
  enabled = true,
  ...props
}: IProps): FetchConversations => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const { setToast } = useToast()
  const { pathname } = useRouter()
  const initialFetchProps: ConversationFetchProps = props.fetchProps || DefaultFetchProps
  const [fetchProps, setFetchProps] = useLocalStorage<ConversationFetchProps>(
    `conversation-filter-${pathname}`,
    initialFetchProps,
    overwriteFilters,
  )
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('')
  const queryKeys = generateListKey({
    type: 'conversation',
    program_id: programId,
    pagination: UnlimitedPagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
    search: deferSearch,
  })
  const {
    data,
    error,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    hasNextPage = false,
  } = useInfiniteQuery<ConversationList, ErrorResponse>(
    queryKeys,
    async ({ signal, pageParam: pg = DefaultMainPagination }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new MessagesApi(generateApiConfig())
      const skip = pg.limit ? pg.page * pg.limit : undefined
      const resp: ConversationList = await requestHandler<ConversationList>(() =>
        client.findConversations(
          {
            program_id: programId,
            limit: pg.limit,
            skip,
            order: fetchProps.order,
            sort: fetchProps.sort,
            search: deferSearch || undefined,
            conversation_id: fetchProps.filter?.conversation_id || undefined,
            has_recieved: fetchProps.filter?.has_recieved || undefined,
            has_sent: fetchProps.filter?.has_sent || undefined,
            is_open:
              typeof fetchProps.filter?.is_open !== 'undefined'
                ? fetchProps.filter?.is_open
                : undefined,
            is_archived:
              typeof fetchProps.filter?.is_archived !== 'undefined'
                ? fetchProps.filter?.is_archived
                : undefined,
            context:
              fetchProps.filter?.context && fetchProps.filter?.context.length > 0
                ? fetchProps.filter.context
                : undefined,
          },
          { signal },
        ),
      )
      return resp
    },
    {
      getNextPageParam: (resp: ConversationList): Pagination | undefined => {
        if (!resp.page_count || resp.page < resp.page_count - 1) {
          return {
            ...DefaultMainPagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page + 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      getPreviousPageParam: (resp: ConversationList): Pagination | undefined => {
        if (!resp.page_count || resp.page <= resp.page_count) {
          return {
            ...DefaultMainPagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page - 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      enabled: !!programId && enabled,
    },
  )

  const handleMarkAsOpen = async (conversationId: string) => {
    if (programId && conversationId) {
      const client = new MessagesApi(generateApiConfig())
      try {
        await requestHandler<Conversation>(
          () =>
            client.markAsOpenConversation({
              program_id: programId,
              conversation_id: conversationId,
            }),
          {
            toast: {
              setToast,
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'conversation',
            program_id: programId,
          }),
        )
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleBulkMarkAsOpen = async (conversations: Conversation[], update_all?: boolean) => {
    if (programId && (conversations.length > 0 || update_all)) {
      const client = new MessagesApi(generateApiConfig())
      try {
        const operation = await requestHandler<BatchConversationOperation>(() =>
          client.markBulkAsOpenConversation({
            program_id: programId,
            ConversationBulkParams: {
              update_all,
              conversation_ids: !update_all ? conversations.map((e) => e.id) : [],
            },
            search: deferSearch || undefined,
            conversation_id: fetchProps.filter?.conversation_id || undefined,
            has_recieved: fetchProps.filter?.has_recieved || undefined,
            has_sent: fetchProps.filter?.has_sent || undefined,
            is_open:
              typeof fetchProps.filter?.is_open !== 'undefined'
                ? fetchProps.filter?.is_open
                : undefined,
            is_archived:
              typeof fetchProps.filter?.is_archived !== 'undefined'
                ? fetchProps.filter?.is_archived
                : undefined,
            context:
              fetchProps.filter?.context && fetchProps.filter?.context.length > 0
                ? fetchProps.filter.context
                : undefined,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'conversation',
            program_id: programId,
          }),
        )
        if (operation.errored_operations === 0) {
          setToast?.({
            header: (translations as any)['success.default_toast_header'],
            message: (translations as any)['success.default_toast_message'],
            type: 'success',
          })
        } else {
          setToast?.({
            header: (translations as any)['error.default_toast_header'],
            message: (translations as any)['error.update_bulk_events'],
            type: 'danger',
          })
        }
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleMarkAsClosed = async (conversationId: string) => {
    if (programId && conversationId) {
      const client = new MessagesApi(generateApiConfig())
      try {
        await requestHandler<Conversation>(
          () =>
            client.markAsClosedConversation({
              program_id: programId,
              conversation_id: conversationId,
            }),
          {
            toast: {
              setToast,
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'conversation',
            program_id: programId,
          }),
        )
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleBulkMarkAsClosed = async (conversations: Conversation[], update_all?: boolean) => {
    if (programId && (conversations.length > 0 || update_all)) {
      const client = new MessagesApi(generateApiConfig())
      try {
        const operation = await requestHandler<BatchConversationOperation>(() =>
          client.markBulkAsClosedConversation({
            program_id: programId,
            ConversationBulkParams: {
              update_all,
              conversation_ids: !update_all ? conversations.map((e) => e.id) : [],
            },
            search: deferSearch || undefined,
            conversation_id: fetchProps.filter?.conversation_id || undefined,
            has_recieved: fetchProps.filter?.has_recieved || undefined,
            has_sent: fetchProps.filter?.has_sent || undefined,
            is_open:
              typeof fetchProps.filter?.is_open !== 'undefined'
                ? fetchProps.filter?.is_open
                : undefined,
            is_archived:
              typeof fetchProps.filter?.is_archived !== 'undefined'
                ? fetchProps.filter?.is_archived
                : undefined,
            context:
              fetchProps.filter?.context && fetchProps.filter?.context.length > 0
                ? fetchProps.filter.context
                : undefined,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'conversation',
            program_id: programId,
          }),
        )
        if (operation.errored_operations === 0) {
          setToast?.({
            header: (translations as any)['success.default_toast_header'],
            message: (translations as any)['success.default_toast_message'],
            type: 'success',
          })
        } else {
          setToast?.({
            header: (translations as any)['error.default_toast_header'],
            message: (translations as any)['error.update_bulk_events'],
            type: 'danger',
          })
        }
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleArchive = async (conversationId: string) => {
    if (programId && conversationId) {
      const client = new MessagesApi(generateApiConfig())
      try {
        await requestHandler<Conversation>(
          () =>
            client.archiveConversation({ program_id: programId, conversation_id: conversationId }),
          {
            toast: {
              setToast,
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'conversation',
            program_id: programId,
          }),
        )
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleBulkArchive = async (conversations: Conversation[], update_all?: boolean) => {
    if (programId && (conversations.length > 0 || update_all)) {
      const client = new MessagesApi(generateApiConfig())
      try {
        const operation = await requestHandler<BatchConversationOperation>(() =>
          client.archiveBulkConversation({
            program_id: programId,
            ConversationBulkParams: {
              update_all,
              conversation_ids: !update_all ? conversations.map((e) => e.id) : [],
            },
            search: deferSearch || undefined,
            conversation_id: fetchProps.filter?.conversation_id || undefined,
            has_recieved: fetchProps.filter?.has_recieved || undefined,
            has_sent: fetchProps.filter?.has_sent || undefined,
            is_open:
              typeof fetchProps.filter?.is_open !== 'undefined'
                ? fetchProps.filter?.is_open
                : undefined,
            is_archived:
              typeof fetchProps.filter?.is_archived !== 'undefined'
                ? fetchProps.filter?.is_archived
                : undefined,
            context:
              fetchProps.filter?.context && fetchProps.filter?.context.length > 0
                ? fetchProps.filter.context
                : undefined,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'conversation',
            program_id: programId,
          }),
        )
        if (operation.errored_operations === 0) {
          setToast?.({
            header: (translations as any)['success.default_toast_header'],
            message: (translations as any)['success.default_toast_message'],
            type: 'success',
          })
        } else {
          setToast?.({
            header: (translations as any)['error.default_toast_header'],
            message: (translations as any)['error.update_bulk_events'],
            type: 'danger',
          })
        }
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleUnArchive = async (conversationId: string) => {
    if (programId && conversationId) {
      const client = new MessagesApi(generateApiConfig())
      try {
        await requestHandler<Conversation>(
          () =>
            client.unarchiveConversation({
              program_id: programId,
              conversation_id: conversationId,
            }),
          {
            toast: {
              setToast,
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'conversation',
            program_id: programId,
          }),
        )
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleBulkUnArchive = async (conversations: Conversation[], update_all?: boolean) => {
    if (programId && (conversations.length > 0 || update_all)) {
      const client = new MessagesApi(generateApiConfig())
      try {
        const operation = await requestHandler<BatchConversationOperation>(() =>
          client.unarchiveBulkConversation({
            program_id: programId,
            ConversationBulkParams: {
              update_all,
              conversation_ids: !update_all ? conversations.map((e) => e.id) : [],
            },
            search: deferSearch || undefined,
            conversation_id: fetchProps.filter?.conversation_id || undefined,
            has_recieved: fetchProps.filter?.has_recieved || undefined,
            has_sent: fetchProps.filter?.has_sent || undefined,
            is_open:
              typeof fetchProps.filter?.is_open !== 'undefined'
                ? fetchProps.filter?.is_open
                : undefined,
            is_archived:
              typeof fetchProps.filter?.is_archived !== 'undefined'
                ? fetchProps.filter?.is_archived
                : undefined,
            context:
              fetchProps.filter?.context && fetchProps.filter?.context.length > 0
                ? fetchProps.filter.context
                : undefined,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'conversation',
            program_id: programId,
          }),
        )
        if (operation.errored_operations === 0) {
          setToast?.({
            header: (translations as any)['success.default_toast_header'],
            message: (translations as any)['success.default_toast_message'],
            type: 'success',
          })
        } else {
          setToast?.({
            header: (translations as any)['error.default_toast_header'],
            message: (translations as any)['error.update_bulk_events'],
            type: 'danger',
          })
        }
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const _setFetchProps = (val?: ConversationFetchProps) => {
    return setFetchProps(val || DefaultFetchProps)
  }

  const resp = [...(data?.pages || [])].pop()
  return {
    data: (data?.pages || []).reduce<Conversation[]>((list, p) => [...list, ...p.data], []) || [],
    total: data?.pages[0].total || 0,
    error,
    fetchProps,
    isError,
    isFetching: isFetching || isFetchingNextPage || isLoading || isSearching,
    isSearching,
    search,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    hasNextPage,
    fetchNextPage,
    handleArchive,
    handleBulkArchive,
    handleBulkMarkAsClosed,
    handleBulkMarkAsOpen,
    handleBulkUnArchive,
    handleMarkAsClosed,
    handleMarkAsOpen,
    handleUnArchive,
    setFetchProps: _setFetchProps,
    setSearch,
  }
}
